import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import * as authApi from '../api/auth';
import { User as SharedUser, CalendarProgress, CourseProgress, CourseLesson } from '@shared/types';

// Frontend User type with string dates (as received from API)
interface User extends Omit<SharedUser, 'purchaseDate' | 'lastLoginDate' | 'createdAt' | 'updatedAt'> {
  purchaseDate: string;
  lastLoginDate?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  calendarProgress: CalendarProgress[];
  courseProgress: CourseProgress[];
  courseLessons: CourseLesson[];
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ message?: string; messageHe?: string; version?: string }>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
  setAuthFromToken: (token: string) => Promise<void>;
  setAuthFromSessionData: (sessionData: {
    user: User;
    token: string;
    calendarProgress?: CalendarProgress[];
    courseProgress?: CourseProgress[];
    courseLessons?: CourseLesson[];
  }) => void;
  updateCalendarProgress: (progress: CalendarProgress[]) => void;
  updateCourseProgress: (progress: CourseProgress[]) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [calendarProgress, setCalendarProgress] = useState<CalendarProgress[]>([]);
  const [courseProgress, setCourseProgress] = useState<CourseProgress[]>([]);
  const [courseLessons, setCourseLessons] = useState<CourseLesson[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    checkSession();
  }, []);

  const checkSession = async () => {
    try {
      setIsLoading(true);
      const session = await authApi.validateSession();
      
      if (session && session.data) {
        setUser(session.data.user);
        setToken(session.data.token || null);
        setCalendarProgress(session.data.calendarProgress || []);
        setCourseProgress(session.data.courseProgress || []);
        setCourseLessons(session.data.courseLessons || []);
      } else {
        setUser(null);
        setToken(null);
        setCalendarProgress([]);
        setCourseProgress([]);
        setCourseLessons([]);
      }
    } catch (error) {
      console.error('Session validation failed:', error);
      setUser(null);
      setToken(null);
      setCalendarProgress([]);
      setCourseProgress([]);
      setCourseLessons([]);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await authApi.login(email, password);
      
      if (response.success && response.user) {
        setUser(response.user);
        setToken(response.token || null);
        setCalendarProgress(response.calendarProgress || []);
        setCourseProgress(response.courseProgress || []);
        setCourseLessons(response.courseLessons || []);
        return {
          message: response.message,
          messageHe: response.messageHe,
          version: response.version
        };
      } else {
        throw new Error('Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setToken(null);
      setCalendarProgress([]);
      setCourseProgress([]);
      setCourseLessons([]);
    }
  };

  const updateCalendarProgress = (progress: CalendarProgress[]) => {
    setCalendarProgress(progress);
  };

  const updateCourseProgress = (progress: CourseProgress[]) => {
    setCourseProgress(progress);
  };

  const refreshSession = async () => {
    await checkSession();
  };

  const setAuthFromToken = async (authToken: string) => {
    try {
      console.log('Setting auth token in cookie...');
      
      // Try multiple cookie formats to ensure compatibility
      const cookieOptions = [
        `token=${authToken}; Secure; SameSite=None; Max-Age=${7 * 24 * 60 * 60}; Path=/; Domain=.netasfactory.co.il`,
        `token=${authToken}; Secure; SameSite=None; Max-Age=${7 * 24 * 60 * 60}; Path=/`,
        `token=${authToken}; Max-Age=${7 * 24 * 60 * 60}; Path=/`,
      ];
      
      // Set all cookie variations
      cookieOptions.forEach(cookie => {
        document.cookie = cookie;
      });
      
      console.log('Cookie set, waiting before validation...');
      
      // Wait a moment for the cookie to be set
      await new Promise(resolve => setTimeout(resolve, 500));
      
      console.log('Attempting to validate session...');
      
      // Then refresh the session to load user data
      const session = await authApi.validateSession();
      
      if (session && session.data && session.data.user) {
        console.log('Session validated successfully, setting user state');
        setUser(session.data.user);
        setToken(session.data.token || authToken);
        setCalendarProgress(session.data.calendarProgress || []);
        setCourseProgress(session.data.courseProgress || []);
        setCourseLessons(session.data.courseLessons || []);
      } else {
        console.error('Session validation returned no user data');
        throw new Error('Failed to authenticate user after setting token');
      }
    } catch (error) {
      console.error('Failed to set auth from token:', error);
      throw error;
    }
  };

  const setAuthFromSessionData = (sessionData: {
    user: User;
    token: string;
    calendarProgress?: CalendarProgress[];
    courseProgress?: CourseProgress[];
    courseLessons?: CourseLesson[];
  }) => {
    console.log('Setting auth from session data:', {
      hasUser: !!sessionData.user,
      hasToken: !!sessionData.token,
      email: sessionData.user?.email,
    });
    
    setUser(sessionData.user);
    setToken(sessionData.token);
    setCalendarProgress(sessionData.calendarProgress || []);
    setCourseProgress(sessionData.courseProgress || []);
    setCourseLessons(sessionData.courseLessons || []);
    
    console.log('Auth state updated successfully');
  };

  const value: AuthContextType = {
    user,
    token,
    calendarProgress,
    courseProgress,
    courseLessons,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    refreshSession,
    setAuthFromToken,
    setAuthFromSessionData,
    updateCalendarProgress,
    updateCourseProgress,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
